import type { ChangeEvent } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { Icon } from "@/react/components/shared/Icon";
import { TippyV2 } from "@/react/components/shared/TippyV2";
import { AiAgentSelect } from "@/react/components/shared/uikit/AiAgentSelect";
import { Typography } from "@/react/components/shared/uikit/Typography";
import { TextAreaWithCounter } from "@/react/pages/BotBuilder/BehaviorPage/TextAreaWithCounter";
import { Form } from "@circle-react-uikit/Form";
import { useCurrentAction } from "../../../ActionRule";
import { useDefaultValueForField } from "../../../useDefaultValueforField";
import "./styles.scss";

const responseRateOptions = Array.from({ length: 10 }, (_, i) => ({
  text: `${100 - i * 10}%`,
  value: 100 - i * 10,
}));

export const FormPostComment = () => {
  const { paramsPath } = useCurrentAction();

  const { getValues, setValue } = useFormContext();

  const selectedId = getValues(`${paramsPath}.community_bot_profile_id`) as
    | number
    | null;

  const responseRate = getValues(`${paramsPath}.response_rate`) as
    | number
    | null;

  const additionalInstructions = getValues(
    `${paramsPath}.additional_instructions`,
  ) as string | null;

  useDefaultValueForField(`${paramsPath}.community_bot_profile_id`, null);
  useDefaultValueForField(`${paramsPath}.response_rate`, 100);
  useDefaultValueForField(`${paramsPath}.additional_instructions`, "");
  useDefaultValueForField(`${paramsPath}.skip_if_answered`, true);

  return (
    <div className="workflows-form-post-comment flex flex-col gap-5">
      <Form.Item
        name={`${paramsPath}.community_bot_profile_id`}
        hideBorder
        fullWidth
        hideDescription
        label={t("settings.workflows.form.agent")}
        labelClassName="!mb-2 !text-sm"
        className="!py-0"
      >
        <AiAgentSelect
          selectedId={selectedId}
          setValue={(value: number) =>
            setValue(`${paramsPath}.community_bot_profile_id`, value)
          }
          placeholderKey="settings.workflows.form.select_agent"
          notFoundKey="settings.workflows.form.no_agents_found"
          enabledStatus
        />
      </Form.Item>
      <Form.Item
        name={`${paramsPath}.response_rate`}
        hideBorder
        fullWidth
        hideDescription
        label={
          <div className="mb-0.5 flex items-center gap-[3px]">
            <span>{t("settings.workflows.form.response_rate")} </span>
            <TippyV2
              content={t("settings.workflows.form.response_rate_description")}
              className="flex"
            >
              <Icon
                type="16-question-mark"
                size={16}
                useWithStrokeCurrentColor
              />
            </TippyV2>
          </div>
        }
        labelClassName="!mb-2 !text-sm"
        className="!py-0"
      >
        <Form.Select
          value={responseRate ?? 100}
          onChange={(e: { value: number }) =>
            setValue(`${paramsPath}.response_rate`, Number(e.value))
          }
          data={responseRateOptions}
          name={`${paramsPath}.response_rate`}
        />
      </Form.Item>
      <Form.Item
        name={`${paramsPath}.additional_instructions`}
        hideBorder
        fullWidth
        hideDescription
        label={t("settings.workflows.form.additional_instructions")}
        labelClassName="!mb-2 !text-sm"
        className="!py-0"
        placeholder={t(
          "settings.workflows.form.additional_instructions_placeholder",
        )}
        rules={{
          required: false,
        }}
      >
        <TextAreaWithCounter
          value={additionalInstructions}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            setValue(`${paramsPath}.additional_instructions`, e.target.value)
          }
          name={`${paramsPath}.additional_instructions`}
          rows={4}
          maxLength={1500}
        />
      </Form.Item>
      <Form.Item
        name={`${paramsPath}.skip_if_answered`}
        hideBorder
        fullWidth
        hideDescription
        hideLabel
        className="!py-0"
        rules={{
          required: false,
        }}
      >
        <div className="flex items-center">
          <span className="mr-4">
            <Form.ToggleSwitch
              variant="small"
              name={`${paramsPath}.skip_if_answered`}
              onChange={e =>
                setValue(`${paramsPath}.skip_if_answered`, e.target.value)
              }
            />
          </span>
          <Typography.LabelSm weight="semibold">
            {t("settings.workflows.form.skip_if_answered")}
          </Typography.LabelSm>

          <TippyV2
            content={t("settings.workflows.form.skip_if_answered_tooltip")}
            arrow={false}
            className="flex"
          >
            <Icon
              type="16-question-mark"
              className="ml-0.5"
              size={16}
              useWithStrokeCurrentColor
            />
          </TippyV2>
        </div>
      </Form.Item>
    </div>
  );
};
